import { useCallback, useMemo, useRef, useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, LinearProgress } from '@mui/material';
import { stripDiacritics } from '@realadvisor/transform/strip-diacritics';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type BuyerLeadDnDColumnFragment,
  type DevelopmentBuyerLeadsActionToolbarFragment,
  type EmailTemplateFormListingFragment,
} from '@graphql/__generated__/graphql';
import {
  BuyerLeadActionToolbar,
  isDevelopment,
} from '@modules/_shared/components/buyer-lead/BuyerLeadActionToolbar';
import { BuyerLeadAddModal } from '@modules/_shared/components/buyer-lead/BuyerLeadAddModal';
import { BuyerLeadDnDColumn } from '@modules/_shared/components/buyer-lead/BuyerLeadDnDColumn';
import { BuyerLeadEditDrawer } from '@modules/_shared/components/buyer-lead/BuyerLeadEditDrawer';
import { GET_BUYER_LEAD_STAGES } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import {
  buyerLeadKanbanReorderingDisabled,
  createBuyerLeadKanbanColumnDefinitions,
  getBuyerLeadKanbanDisplayName,
  getBuyerLeadKanbanLiveRegionAnnouncement,
  getBuyerLeadKanbanStages,
  hasBuyerLeadKanbanStageChanged,
  withCurrentBuyersWhere,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { KanbanBoard } from '@modules/_shared/components/kanban/KanbanBoard';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canEditListing } from '@modules/_shared/utils/listingPermissions';

const UPDATE_LEAD_STAGE = gql(/* GraphQL */ `
  mutation UpdateBuyerLeadStageKanban(
    $id: uuid!
    $updates: buyer_leads_set_input!
  ) {
    update_buyer_leads_by_pk(pk_columns: { id: $id }, _set: $updates) {
      id
      ...BuyerLeadDnDColumn
      lead_stage {
        id
        name
        label
        order_nr
      }
    }
  }
`);

type BuyerLeadsKanbanProps = {
  refetchQueriesOnAdd?: InternalRefetchQueriesInclude;
  matchedBulkEmailStageId?: string;
  buyerLeads: BuyerLeadDnDColumnFragment[];
  readOnly?: boolean;
  includeLotReference?: boolean;
  offerInOverlay?: boolean;
  extraToolbarActions?: React.ReactNode;
  extraRoutes?: React.ReactNode;
  hideToolbar?: boolean;
  selectedItems?: string[];
  onSelectedItemsChange?: (items: string[]) => void;
  forceSelectionMode?: boolean;
  onEmailCampaignStarted?: (buyerLeadIds: string[]) => void;
  parent:
    | EmailTemplateFormListingFragment
    | DevelopmentBuyerLeadsActionToolbarFragment;
};

const normalizeString = (str: string) => {
  return stripDiacritics(str).toLowerCase();
};

const toSearchToken = (str: string) =>
  normalizeString(str).replace(/[^a-z0-9]/g, '');

export const BuyerLeadsKanban: React.FC<BuyerLeadsKanbanProps> = ({
  parent,
  buyerLeads,
  readOnly = false,
  extraToolbarActions,
  refetchQueriesOnAdd,
  matchedBulkEmailStageId,
  extraRoutes,
  includeLotReference = false,
  offerInOverlay = false,
  hideToolbar = false,
  selectedItems: controlledSelectedItems,
  onSelectedItemsChange,
  forceSelectionMode: controlledForceSelectionMode,
  onEmailCampaignStarted,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();

  const { data, error, loading } = useQuery(GET_BUYER_LEAD_STAGES);

  const canEditLead = useCallback(
    (lead: BuyerLeadDnDColumnFragment) =>
      !readOnly && canEditListing(me, lead.lot ?? null),
    [me, readOnly],
  );

  const [filter, setFilter] = useState<string>('');
  const [internalSelectedItems, setInternalSelectedItems] = useState<string[]>(
    [],
  );
  const [internalSelectionModeForced, setInternalSelectionModeForced] =
    useState(false);
  const selectedItems = controlledSelectedItems ?? internalSelectedItems;
  const setSelectedItems = onSelectedItemsChange ?? setInternalSelectedItems;
  const isSelectionModeForced =
    controlledForceSelectionMode ?? internalSelectionModeForced;
  const [updateLeadStage] = useMutation(UPDATE_LEAD_STAGE);

  const [activeStageId, setActiveStageId] = useState<string | null>(null);
  const [updateOrderError, setUpdateOrderError] = useState<ErrorLike | null>(
    null,
  );
  const addModalOpened = activeStageId != null;

  const filteredBuyerLeads = useMemo(() => {
    const searchTokens = filter.split(/\s+/).map(toSearchToken).filter(Boolean);

    if (searchTokens.length === 0) {
      return buyerLeads;
    }

    return buyerLeads.filter(lead => {
      const haystack = toSearchToken(
        [
          lead.user.first_name,
          lead.user.last_name,
          lead.user.full_name,
          lead.user.emails[0]?.email,
        ]
          .filter(Boolean)
          .join(' '),
      );

      return searchTokens.every(token => haystack.includes(token));
    });
  }, [buyerLeads, filter]);

  const initialSearchParams = useRef(searchParams);
  const columnsDefinition = useMemo(
    () =>
      createBuyerLeadKanbanColumnDefinitions<BuyerLeadDnDColumnFragment>(
        data?.lead_stages,
        ({ index, column, items }) => (
          <BuyerLeadDnDColumn
            key={column.id}
            index={index}
            column={column}
            items={items}
            includeLotReference={includeLotReference}
            readOnly={readOnly}
            canEditLead={canEditLead}
            onItemClicked={
              readOnly
                ? undefined
                : lead => {
                    navigate({
                      pathname: `./${lead.id}`,
                      search: withCurrentBuyersWhere(
                        initialSearchParams.current,
                        searchParams,
                      ).toString(),
                    });
                  }
            }
            onNewBtnClicked={() => {
              setActiveStageId(column.id);
            }}
          />
        ),
      ),
    [
      data?.lead_stages,
      navigate,
      includeLotReference,
      readOnly,
      canEditLead,
      searchParams,
    ],
  );

  const stages = useMemo(
    () => getBuyerLeadKanbanStages(data?.lead_stages),
    [data?.lead_stages],
  );

  const buyerLeadAddModalParentProps:
    | {
        lotId: string;
        developmentId?: never;
      }
    | {
        developmentId: string;
        lotId?: never;
      } = isDevelopment(parent)
    ? {
        developmentId: parent.id,
      }
    : {
        lotId: parent.id,
      };

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      {loading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!hideToolbar && (
            <BuyerLeadActionToolbar
              parent={parent}
              buyerLeads={buyerLeads}
              readOnly={readOnly}
              onFilterChange={setFilter}
              selectedLeads={selectedItems}
              forceSelectionMode={isSelectionModeForced}
              toggleSelectionMode={() =>
                setInternalSelectionModeForced(prev => !prev)
              }
              buyerLeadsCount={filteredBuyerLeads.length}
              matchedBulkEmailStageId={matchedBulkEmailStageId}
              onEmailCampaignStarted={onEmailCampaignStarted}
              onReset={() => {
                setSelectedItems([]);
                setInternalSelectionModeForced(false);
              }}
              columns={stages}
            >
              {extraToolbarActions}
            </BuyerLeadActionToolbar>
          )}
          <KanbanBoard
            items={filteredBuyerLeads}
            getItemId={buyerLead => buyerLead.id}
            selectedItems={selectedItems}
            onItemsSelected={items => {
              const allowed = items
                .map(id => buyerLeads.find(lead => lead.id === id.toString()))
                .filter(
                  (lead): lead is BuyerLeadDnDColumnFragment =>
                    lead != null && canEditLead(lead),
                )
                .map(lead => lead.id);
              setSelectedItems(allowed);
            }}
            forceSelectionMode={isSelectionModeForced}
            instanceId="buyer-lead-kanban"
            disableReorder={buyerLeadKanbanReorderingDisabled}
            disableDnd={readOnly}
            defaultPositionInNewColumn="top"
            getLiveRegionAnnouncements={args => {
              const { item } = args;

              return getBuyerLeadKanbanLiveRegionAnnouncement(
                args,
                getBuyerLeadKanbanDisplayName(item),
              );
            }}
            onItemMoved={(item, columnId) => {
              if (readOnly || !canEditLead(item)) {
                return;
              }

              if (!hasBuyerLeadKanbanStageChanged(item, columnId)) {
                // if the item is not moved to a different stage, we don't need to update the order
                return;
              }

              setUpdateOrderError(null);

              updateLeadStage({
                variables: {
                  id: item.id,
                  updates: {
                    stage_id: columnId.toString(),
                  },
                },
                onError: error => {
                  setUpdateOrderError(error);
                  console.error('updateLeadOrderAndStage error', error);
                },
              });
            }}
            columns={columnsDefinition}
          />
          <BuyerLeadAddModal
            {...buyerLeadAddModalParentProps}
            destinationStageId={activeStageId}
            opened={addModalOpened}
            onClose={() => setActiveStageId(null)}
            refetchQueries={refetchQueriesOnAdd}
          />
          <MutationErrorSnackbar
            error={updateOrderError}
            onClose={() => setUpdateOrderError(null)}
          />
        </Box>
      )}
      <Routes>
        {extraRoutes}
        <Route
          path=":buyerLeadId"
          element={
            <BuyerLeadEditDrawer
              showListingCard={includeLotReference}
              offerInOverlay={offerInOverlay}
              readOnly={readOnly}
              onClose={() => {
                navigate({
                  pathname: '.',
                  search: withCurrentBuyersWhere(
                    initialSearchParams.current,
                    searchParams,
                  ).toString(),
                });
              }}
            />
          }
        />
      </Routes>
    </>
  );
};
